import ModellerDefaults from '../../ModellerDefaults';

// SVG data URIs for triangle and half circle icons
const triangleBase64 = 'PHN2ZyB3aWR0aD0iMTUiIGhlaWdodD0iMTUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+'
  + 'PHBvbHlnb24gcG9pbnRzPSIwLDAgMTUsNy41IDAsMTUiIGZpbGw9IiMzMzMiLz48L3N2Zz4=';
const triangleSvg = `data:image/svg+xml;base64,${triangleBase64}`;

const halfCircleBase64 = 'PHN2ZyB3aWR0aD0iNy41IiBoZWlnaHQ9IjE1IiB2aWV3Qm94PSIwIDAgNy41IDE1IiB4bWxucz0iaHR0cDovL3d3dy53My5v'
  + 'cmcvMjAwMC9zdmciPgogIDxwYXRoIGQ9Ik0gMCAwIEEgNy41IDcuNSAwIDAgMSAwIDE1IEwgMCAwIFoiIGZpbGw9IiMzMzMiLz4KPC9zdmc+';

const halfCircleSvg = `data:image/svg+xml;base64,${halfCircleBase64}`;

const fitFontSizeToWidth = (node, options = {}) => {
  const {
    maxSize = 12,
    minSize = 8,
    padding = 8,
    avgCharWidth = 0.6,
  } = options;

  const label = node?.data?.('name') || '';
  const parent = node?.parent?.();
  const width = (parent && parent.length ? parent.width() : node?.width?.()) || 0;
  if (!label || width <= padding) {
    return maxSize;
  }

  const estimatedSize = Math.floor((width - padding) / (label.length * avgCharWidth));
  return Math.max(minSize, Math.min(maxSize, estimatedSize));
};

const fitFontSizeToNodeWidth = (node, options = {}) => {
  const {
    maxSize = 12,
    minSize = 9,
    padding = 6,
    avgCharWidth = 0.52,
  } = options;

  const label = node?.data?.('name') || '';
  const width = node?.width?.() || 0;
  if (!label || width <= padding) {
    return maxSize;
  }

  const estimatedSize = Math.floor((width - padding) / (label.length * avgCharWidth));
  return Math.max(minSize, Math.min(maxSize, estimatedSize));
};


/**
 * Default styles for regular network graphs (flat model and individual networks)
 */
const getDefaultGraphStyles = () => [
  {
    default: true,
    selector: 'node.primary',
    style: {
      width: `${ModellerDefaults.DefaultGraphics.graph.width}px`,
      height: `${ModellerDefaults.DefaultGraphics.graph.height}px`,
      'background-color': 'white',
      'text-valign': 'center',
      'text-halign': 'center',
      content: 'data(name)',
      'text-wrap': 'wrap',
      'text-max-width': (node) => node.width() - 4,
      'border-width': '1px',
      'border-color': 'black',
      padding: '5px',
    },
  },
  {
    default: true,
    selector: 'edge',
    style: {
      'target-arrow-shape': 'triangle',
      'target-arrow-color': 'black',
      'source-arrow-color': 'black',
      'line-color': '#333',
      width: 1.5,
      'curve-style': 'bezier',
      'arrow-scale': 1.5,
    },
  },
  {
    default: true,
    selector: 'edge.dashed',
    style: {
      'line-style': 'dashed',
    },
  },
  {
    default: true,
    selector: 'node.primary:selected',
    style: {
      'outline-offset': '10px',
      'outline-width': '1px',
      'outline-opacity': 1,
      'outline-color': 'black',
    },
  },
  {
    default: true,
    selector: 'node.hidden-to-show',
    style: {
      'border-style': 'dashed',
    },
  },
  {
    default: true,
    selector: `node.primary.mode_${ModellerDefaults.NodeModes.CHART}`,
    style: {
      shape: 'rectangle',
      'background-fit': 'contain',
      'background-repeat': 'no-repeat',
      'background-width-relative-to': 'inner',
      'background-height-relative-to': 'inner',
      'background-width': '100%',
      'background-height': '100%',
      'background-offset-y': '25px',
      // padding: '0px',
      'padding-left': '0px',
      'padding-right': '0px',
      height: `${ModellerDefaults.DefaultGraphics.graph.height + ModellerDefaults.GraphDefaults.ChartModeExtraHeight}px`,
      'text-valign': 'center',
      'text-margin-y': (node) => -node.height() / 2 + 10,
      'text-wrap': 'ellipsis',
    },
  },
  {
    default: true,
    selector: 'node.wrapper',
    style: {
      'border-width': '0px',
      'background-opacity': 0,
    },
  },
  {
    default: true,
    selector: 'node.hidden',
    style: {
      display: 'none',
    },
  },
  {
    default: true,
    selector: 'node.observation',
    style: {
      shape: 'rectangle',
      'background-color': 'data(color)',
      'text-valign': 'center',
      'text-halign': 'right',
      content: 'data(name)',
      'text-wrap': 'none',
      'text-max-width': '9999px',
      'border-width': '1px',
      width: '20px',
      height: '20px',
      'text-margin-x': '5px',
      'text-background-padding': '5px',
      'text-background-color': 'white',
      'text-background-opacity': 0.8,
      fontSize: '10px',
    },
  },
];

/**
 * Default styles for model view graph (shows networks as nodes with input/output ports)
 */
const getModelViewStyles = () => [
  {
    selector: 'node.network-node',
    style: {
      shape: 'rectangle',
      'background-color': 'white',
      'border-width': '1px',
      'border-color': '#333',
      width: 'data(width)',
      height: 'data(height)',
      'min-width': 'data(width)',
      'min-height': 'data(height)',
      padding: 0,
      'compound-sizing-wrt-labels': 'exclude',
      'text-valign': 'top',
      'text-halign': 'center',
    },
  },
  {
    selector: 'node.network-name',
    style: {
      shape: 'rectangle',
      'background-color': 'white',
      'border-width': '0px',
      // 'border-color': '#ccc',
      'font-weight': 400,
      width: 'data(width)',
      height: '30px',
      'text-valign': 'center',
      'text-halign': 'center',
      content: 'data(name)',
      'text-wrap': 'ellipsis',
      'text-max-width': 'data(width)',
      'font-size': (node) => fitFontSizeToWidth(node, {
        minSize: 9,
        maxSize: 13,
        padding: 4,
        avgCharWidth: 0.52,
      }),
      events: 'no',
    },
  },
  {
    selector: 'node.input-node',
    style: {
      shape: 'rectangle',
      'background-color': '#b3d9ff',
      'border-width': '1px',
      'border-color': '#333',
      width: 'data(width)',
      height: 'data(height)',
      'text-valign': 'center',
      'text-halign': 'center',
      content: 'data(name)',
      'text-wrap': 'none',
      'text-max-width': 'data(width)',
      'font-size': (node) => fitFontSizeToNodeWidth(node, { minSize: 9, maxSize: 12 }),
      events: 'no',
      // 'text-overflow-wrap': 'none',
    },
  },
  {
    selector: 'node.input-icon',
    style: {
      shape: 'rectangle',
      width: '15px',
      height: '15px',
      'background-color': 'white',
      'border-width': '0px',
      'background-image': triangleSvg,
      'background-fit': 'contain',
      'background-width': '15px',
      'background-height': '15px',
      // 'background-position': 'center',
      events: 'no',
    },
  },
  {
    selector: 'node.output-node',
    style: {
      shape: 'rectangle',
      'background-color': '#b3ffb3',
      'border-width': '1px',
      'border-color': '#333',
      width: 'data(width)',
      height: 'data(height)',
      'text-valign': 'center',
      'text-halign': 'center',
      content: 'data(name)',
      'text-wrap': 'none',
      'text-max-width': 'data(width)',
      'font-size': (node) => fitFontSizeToNodeWidth(node, { minSize: 9, maxSize: 12 }),
      events: 'no',
      // 'text-overflow-wrap': 'none',
    },
  },
  {
    selector: 'node.output-icon',
    style: {
      shape: 'rectangle',
      width: '15px',
      height: '15px',
      'background-color': 'white',
      'border-width': '0px',
      'background-image': halfCircleSvg,
      'background-fit': 'contain',
      'background-width': '15px',
      'background-height': '15px',
      // 'background-position': 'center',
      events: 'no',
    },
  },
  {
    selector: 'edge',
    style: {
      'target-arrow-shape': 'none',
      'target-arrow-color': 'transparent',
      'source-arrow-color': 'transparent',
      'line-color': '#333',
      width: 1.5,
      'curve-style': 'bezier',
      'z-index-compare': 'manual',
      'z-index': 999,
      'z-compound-depth': 'top',
      'source-endpoint': '0 0',
      'target-endpoint': '0 0',
    },
  },
  {
    selector: 'edge.inter-network-edge',
    style: {
      'line-color': 'transparent',
      'target-arrow-color': 'transparent',
      'source-arrow-color': 'transparent',
      width: 0,
    },
  },
];

export default {
  getDefaultGraphStyles,
  getModelViewStyles,
};
